/**
 * Group locations by their owning business.
 *
 * `GET /v1/locations` returns `business_name` on every row, but the portal's
 * list surfaces (header switcher, Settings → Locations, the notifications
 * selector) all used to drop it and render one flat pool. For a single-business
 * studio that is fine; for an admin assigned to two businesses it makes
 * similarly-named locations in the same metro indistinguishable, and gives no
 * way to answer "which of my businesses is this under?".
 *
 * Ordering is preserved from the input (the API sorts by location name) and
 * groups appear in first-seen order, so a single-business org renders in exactly
 * the order it did before grouping existed.
 */

/** The subset of a location row this module needs. */
export interface GroupableLocation {
  id: string;
  business_id: string;
  business_name: string;
}

export interface LocationGroup<T> {
  businessId: string;
  businessName: string;
  items: T[];
}

export function groupLocationsByBusiness<T extends GroupableLocation>(
  locations: readonly T[],
): LocationGroup<T>[] {
  const groups = new Map<string, LocationGroup<T>>();

  for (const loc of locations) {
    let group = groups.get(loc.business_id);
    if (!group) {
      group = { businessId: loc.business_id, businessName: loc.business_name, items: [] };
      groups.set(loc.business_id, group);
    }
    group.items.push(loc);
  }

  return [...groups.values()];
}

/**
 * Whether the business label is worth showing. One business (or none) means the
 * label is pure noise — every row would carry the same value.
 */
export function shouldShowBusinessGroups(groups: readonly LocationGroup<unknown>[]): boolean {
  return groups.length > 1;
}
